import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Shield, ShieldCheck, ShieldAlert, Lock, KeyRound, Eye, EyeOff,
  Fingerprint, Monitor, Clock, MapPin, LogOut, AlertTriangle,
  CheckCircle2, XCircle, RefreshCw, Download, Database, Users,
  Activity, Globe, Smartphone, Laptop, ChevronRight, Info
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAccounting } from "@/contexts/AccountingContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";

// ── Types ──

interface SessionEntry {
  id: string;
  device: string;
  deviceType: "desktop" | "mobile" | "tablet";
  browser: string;
  ip: string;
  location: string;
  loginAt: string;
  lastActive: string;
  isCurrent: boolean;
  status: "active" | "expired";
}

interface SecurityEvent {
  id: string;
  type: "login" | "logout" | "password_change" | "2fa_toggle" | "failed_login" | "export" | "role_change";
  description: string;
  timestamp: string;
  ip: string;
  severity: "low" | "medium" | "high";
}

interface SecuritySetting {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
  category: "encryption" | "auth" | "monitoring" | "backup";
  icon: React.ComponentType<{ className?: string }>;
}

// ── Security Data & Dynamic State ──

const isMobile = typeof navigator !== "undefined" && /mobile|android|iphone/i.test(navigator.userAgent);
const isMac = typeof navigator !== "undefined" && /mac/i.test(navigator.userAgent);
const browserName = typeof navigator !== "undefined"
  ? navigator.userAgent.includes("Chrome") ? "Chrome" : navigator.userAgent.includes("Firefox") ? "Firefox" : navigator.userAgent.includes("Safari") ? "Safari" : "Browser"
  : "Browser";

const CURRENT_ACTIVE_SESSION: SessionEntry[] = [
  {
    id: "current-session",
    device: isMobile ? "Mobile Device" : isMac ? "MacBook / macOS" : "Desktop PC",
    deviceType: isMobile ? "mobile" : "desktop",
    browser: browserName,
    ip: "Authorized Client",
    location: "India",
    loginAt: new Date().toISOString(),
    lastActive: new Date().toISOString(),
    isCurrent: true,
    status: "active",
  },
];

// ── Components ──

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`p-5 rounded-lg bg-card border border-border shadow-card ${className}`}>{children}</div>
);

const DEVICE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  desktop: Laptop, mobile: Smartphone, tablet: Monitor,
};

const SEVERITY_STYLES: Record<string, { dot: string; text: string }> = {
  low: { dot: "bg-success", text: "text-success" },
  medium: { dot: "bg-warning", text: "text-warning" },
  high: { dot: "bg-destructive", text: "text-destructive" },
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  login: "Login", logout: "Logout", password_change: "Password Change",
  "2fa_toggle": "2FA Update", failed_login: "Failed Login",
  export: "Data Export", role_change: "Role Change",
};

const SecurityPage = () => {
  const { user, role, roleLabel } = useAuth();
  const { auditLog } = useAccounting();

  const [sessions, setSessions] = useState(CURRENT_ACTIVE_SESSION);

  const events: SecurityEvent[] = auditLog.map((log, i) => ({
    id: log.id || `audit-${i}`,
    type: "role_change" as const,
    description: `${log.action}${log.details ? `: ${log.details}` : ""}`,
    timestamp: log.timestamp || new Date().toISOString(),
    ip: "Authorized Client",
    severity: "low" as const,
  }));
  const [settings, setSettings] = useState<SecuritySetting[]>([
    { id: "enc-transit", label: "Encryption in Transit (TLS 1.3)", description: "All data encrypted during transmission using TLS 1.3 protocol", enabled: true, category: "encryption", icon: Lock },
    { id: "enc-rest", label: "Encryption at Rest (AES-256)", description: "Database and file storage encrypted with AES-256 encryption", enabled: true, category: "encryption", icon: Database },
    { id: "enc-fields", label: "Field-Level Encryption", description: "Sensitive fields like GSTIN, PAN, bank details are individually encrypted", enabled: true, category: "encryption", icon: KeyRound },
    { id: "auth-2fa", label: "Two-Factor Authentication", description: "Require OTP via authenticator app or SMS for login", enabled: false, category: "auth", icon: Fingerprint },
    { id: "auth-session", label: "Session Timeout (30 min)", description: "Auto-logout after 30 minutes of inactivity", enabled: true, category: "auth", icon: Clock },
    { id: "auth-ip", label: "IP Whitelisting", description: "Restrict access to specific IP addresses", enabled: false, category: "auth", icon: Globe },
    { id: "auth-lockout", label: "Account Lockout (5 attempts)", description: "Lock account after 5 consecutive failed login attempts", enabled: true, category: "auth", icon: ShieldAlert },
    { id: "mon-audit", label: "Full Audit Trail", description: "Track all user actions with timestamps and IP addresses", enabled: true, category: "monitoring", icon: Activity },
    { id: "mon-anomaly", label: "Anomaly Detection", description: "Alert on unusual login patterns or data access", enabled: true, category: "monitoring", icon: AlertTriangle },
    { id: "mon-realtime", label: "Real-Time Alerts", description: "Instant notifications for critical security events", enabled: true, category: "monitoring", icon: Eye },
    { id: "bak-auto", label: "Automated Daily Backups", description: "Automatic encrypted backups every 24 hours", enabled: true, category: "backup", icon: RefreshCw },
    { id: "bak-geo", label: "Geo-Redundant Storage", description: "Backups replicated across multiple data centers", enabled: true, category: "backup", icon: Globe },
    { id: "bak-export", label: "Manual Backup Export", description: "Download encrypted backup of all financial data", enabled: true, category: "backup", icon: Download },
  ]);

  const securityScore = useMemo(() => {
    const enabled = settings.filter((s) => s.enabled).length;
    return Math.round((enabled / settings.length) * 100);
  }, [settings]);

  const toggleSetting = (id: string) => {
    const setting = settings.find((s) => s.id === id);
    if (!setting) return;

    // Some settings shouldn't be toggled off
    if (["enc-transit", "enc-rest", "mon-audit"].includes(id) && setting.enabled) {
      toast({ title: "Cannot Disable", description: "This security feature is mandatory for compliance.", variant: "destructive" });
      return;
    }

    if (id === "auth-2fa" && !setting.enabled) {
      toast({ title: "2FA Enrollment", description: "Two-factor authentication enabled. You'll need to verify via authenticator app on next login.", });
    }

    setSettings((prev) => prev.map((s) => s.id === id ? { ...s, enabled: !s.enabled } : s));
    toast({ title: setting.enabled ? "Disabled" : "Enabled", description: `${setting.label} has been ${setting.enabled ? "disabled" : "enabled"}.` });
  };

  const revokeSession = (id: string) => {
    setSessions((prev) => prev.filter((s) => s.id !== id));
    toast({ title: "Session Revoked", description: "The session has been terminated." });
  };

  const revokeAllOtherSessions = () => {
    setSessions((prev) => prev.filter((s) => s.isCurrent));
    toast({ title: "All Other Sessions Revoked", description: "Only your current session remains active." });
  };

  const CATEGORY_META: Record<string, { label: string; icon: React.ComponentType<{ className?: string }>; color: string }> = {
    encryption: { label: "Encryption", icon: Lock, color: "text-accent" },
    auth: { label: "Authentication", icon: Fingerprint, color: "text-success" },
    monitoring: { label: "Monitoring", icon: Activity, color: "text-warning" },
    backup: { label: "Backups", icon: Database, color: "text-accent" },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <Shield className="h-6 w-6 text-accent" />
            Security Center
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Monitor and manage your account and data security</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-xs">{roleLabel}</Badge>
        </div>
      </div>

      {/* Security Score + Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="relative overflow-hidden">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${securityScore >= 80 ? "bg-success/10" : securityScore >= 60 ? "bg-warning/10" : "bg-destructive/10"}`}>
                <ShieldCheck className={`h-5 w-5 ${securityScore >= 80 ? "text-success" : securityScore >= 60 ? "text-warning" : "text-destructive"}`} />
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">{securityScore}%</div>
                <div className="text-[11px] text-muted-foreground">Security Score</div>
              </div>
            </div>
            <Progress value={securityScore} className="h-1.5" />
            <p className="text-[11px] text-muted-foreground mt-2">
              {securityScore >= 80 ? "Excellent protection" : securityScore >= 60 ? "Good, but can improve" : "Action required"}
            </p>
          </Card>
        </motion.div>

        {[
          { label: "Active Sessions", value: sessions.filter((s) => s.status === "active").length.toString(), sub: `${sessions.length} total`, icon: Monitor, color: "text-accent" },
          { label: "Security Events", value: events.length.toString(), sub: "Audit trail", icon: Activity, color: "text-warning" },
          { label: "Audit Entries", value: auditLog.length.toString(), sub: "All time", icon: Eye, color: "text-success" },
        ].map((stat, i) => (
          <motion.div key={stat.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i + 1) * 0.06 }}>
            <Card>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{stat.label}</span>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
              <div className="text-2xl font-bold text-foreground">{stat.value}</div>
              <div className="text-[11px] text-muted-foreground">{stat.sub}</div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="settings" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="settings" className="text-xs">Security Settings</TabsTrigger>
          <TabsTrigger value="sessions" className="text-xs">Active Sessions</TabsTrigger>
          <TabsTrigger value="activity" className="text-xs">Security Log</TabsTrigger>
          <TabsTrigger value="roles" className="text-xs">Access Control</TabsTrigger>
        </TabsList>

        {/* Security Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          {(["encryption", "auth", "monitoring", "backup"] as const).map((category) => {
            const meta = CATEGORY_META[category];
            const items = settings.filter((s) => s.category === category);
            return (
              <Card key={category}>
                <div className="flex items-center gap-2 mb-4">
                  <meta.icon className={`h-4 w-4 ${meta.color}`} />
                  <h3 className="text-sm font-semibold text-foreground">{meta.label}</h3>
                  <span className="text-[10px] text-muted-foreground ml-auto">
                    {items.filter((s) => s.enabled).length}/{items.length} active
                  </span>
                </div>
                <div className="space-y-3">
                  {items.map((setting) => (
                    <div key={setting.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50 hover:border-border transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${setting.enabled ? "bg-success/10" : "bg-muted"}`}>
                          <setting.icon className={`h-4 w-4 ${setting.enabled ? "text-success" : "text-muted-foreground"}`} />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-foreground flex items-center gap-2">
                            {setting.label}
                            {["enc-transit", "enc-rest", "mon-audit"].includes(setting.id) && (
                              <Badge variant="outline" className="text-[9px] py-0 h-4">Required</Badge>
                            )}
                          </div>
                          <div className="text-[11px] text-muted-foreground">{setting.description}</div>
                        </div>
                      </div>
                      <Switch checked={setting.enabled} onCheckedChange={() => toggleSetting(setting.id)} />
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{sessions.filter((s) => s.status === "active").length} active sessions</p>
            {sessions.filter((s) => !s.isCurrent && s.status === "active").length > 0 && (
              <button onClick={revokeAllOtherSessions} className="text-xs text-destructive hover:underline font-medium">
                Revoke All Other Sessions
              </button>
            )}
          </div>
          <div className="space-y-3">
            {sessions.map((session, i) => {
              const DeviceIcon = DEVICE_ICONS[session.deviceType] || Monitor;
              return (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card className={session.isCurrent ? "border-accent/30 bg-accent/5" : session.status === "expired" ? "opacity-60" : ""}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${session.isCurrent ? "bg-accent/10" : "bg-muted"}`}>
                          <DeviceIcon className={`h-5 w-5 ${session.isCurrent ? "text-accent" : "text-muted-foreground"}`} />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-foreground flex items-center gap-2">
                            {session.device}
                            {session.isCurrent && <Badge className="text-[9px] py-0 h-4 bg-accent text-accent-foreground">Current</Badge>}
                            {session.status === "expired" && <Badge variant="outline" className="text-[9px] py-0 h-4">Expired</Badge>}
                          </div>
                          <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                            <span>{session.browser}</span>
                            <span>•</span>
                            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{session.location}</span>
                            <span>•</span>
                            <span>IP: {session.ip}</span>
                          </div>
                          <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-3">
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Login: {new Date(session.loginAt).toLocaleString("en-IN")}</span>
                            <span>Last active: {new Date(session.lastActive).toLocaleString("en-IN")}</span>
                          </div>
                        </div>
                      </div>
                      {!session.isCurrent && session.status === "active" && (
                        <button
                          onClick={() => revokeSession(session.id)}
                          className="text-xs text-destructive hover:bg-destructive/10 px-3 py-1.5 rounded-lg transition-colors font-medium"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </TabsContent>

        {/* Security Activity Log */}
        <TabsContent value="activity" className="space-y-4">
          <Card>
            <h3 className="text-sm font-semibold text-foreground mb-4">Recent Security Events</h3>
            <div className="space-y-1">
              {events.length === 0 ? (
                <p className="text-center py-6 text-muted-foreground text-sm">No security events recorded yet.</p>
              ) : (
                events.map((event, i) => {
                  const sev = SEVERITY_STYLES[event.severity];
                  return (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.04 }}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/30 transition-colors"
                    >
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sev.dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                            {EVENT_TYPE_LABELS[event.type] || event.type}
                          </span>
                          <span className={`text-[10px] font-medium ${sev.text}`}>{event.severity}</span>
                        </div>
                        <p className="text-sm text-foreground mt-0.5">{event.description}</p>
                        <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2">
                          <span>{new Date(event.timestamp).toLocaleString("en-IN")}</span>
                          <span>•</span>
                          <span>IP: {event.ip}</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Role-Based Access Control */}
        <TabsContent value="roles" className="space-y-4">
          <Card>
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-4 w-4 text-accent" />
              <h3 className="text-sm font-semibold text-foreground">Role-Based Access Matrix</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">Permission</th>
                    <th className="text-center py-2 px-3 text-xs font-medium text-muted-foreground">Super Admin</th>
                    <th className="text-center py-2 px-3 text-xs font-medium text-muted-foreground">Company Admin</th>
                    <th className="text-center py-2 px-3 text-xs font-medium text-muted-foreground">CA</th>
                    <th className="text-center py-2 px-3 text-xs font-medium text-muted-foreground">Accountant</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { perm: "View Financial Data", sa: true, ca2: true, ca: true, acc: true },
                    { perm: "Create Vouchers", sa: false, ca2: true, ca: true, acc: true },
                    { perm: "Approve / Reject Entries", sa: false, ca2: true, ca: true, acc: false },
                    { perm: "Delete / Cancel Vouchers", sa: false, ca2: true, ca: true, acc: false },
                    { perm: "Manage Users", sa: true, ca2: true, ca: false, acc: false },
                    { perm: "GST Filing", sa: false, ca2: false, ca: true, acc: false },
                    { perm: "Export Data", sa: true, ca2: true, ca: true, acc: false },
                    { perm: "View Audit Logs", sa: true, ca2: true, ca: true, acc: false },
                    { perm: "Manage Companies", sa: true, ca2: true, ca: false, acc: false },
                    { perm: "Security Settings", sa: true, ca2: true, ca: false, acc: false },
                    { perm: "Platform Settings", sa: true, ca2: false, ca: false, acc: false },
                    { perm: "Backup Management", sa: true, ca2: true, ca: false, acc: false },
                  ].map((row) => (
                    <tr key={row.perm} className="border-b border-border/50 hover:bg-muted/20">
                      <td className="py-2.5 px-3 text-foreground text-xs font-medium">{row.perm}</td>
                      {[row.sa, row.ca2, row.ca, row.acc].map((allowed, i) => (
                        <td key={i} className="text-center py-2.5 px-3">
                          {allowed ? (
                            <CheckCircle2 className="h-4 w-4 text-success mx-auto" />
                          ) : (
                            <XCircle className="h-4 w-4 text-muted-foreground/30 mx-auto" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-2 mb-3">
              <Info className="h-4 w-4 text-accent" />
              <h3 className="text-sm font-semibold text-foreground">Data Isolation</h3>
            </div>
            <div className="space-y-2">
              {[
                { label: "Company Data Isolation", desc: "Each company's financial data is completely isolated — no cross-company data leakage" },
                { label: "Row-Level Security", desc: "Database-level policies ensure users only see data they're authorized to access" },
                { label: "API Authorization", desc: "Every API call is validated against user role and company membership" },
                { label: "Audit on Sensitive Ops", desc: "All create, update, delete, and export operations are logged with full context" },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-3 p-3 rounded-lg bg-success/5 border border-success/20">
                  <ShieldCheck className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs font-semibold text-foreground">{item.label}</div>
                    <div className="text-[11px] text-muted-foreground">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SecurityPage;
